import React, { createContext, useContext, useRef } from 'react';

const AnimationContext = createContext();

export const useAnimation = () => {
  const context = useContext(AnimationContext);
  if (!context) {
    throw new Error('useAnimation must be used within AnimationProvider');
  }
  return context;
};

export const AnimationProvider = ({ children }) => {
  // Animation state and buffers refs
  const playerAnimationState = useRef(new Map()); // playerId -> {direction, frame, lastMoveTime}
  const playerStateBuffers = useRef(new Map()); // playerId -> [{ time, x, y }]
  const bombStateBuffers = useRef(new Map()); // bombId -> [{ time, pixelX, pixelY }]

  // Animation actions
  const actions = React.useMemo(() => ({
    // Player animation utilities
    getPlayerAnimationState: (playerId) => {
      return playerAnimationState.current.get(playerId) || {
        direction: 'front',
        frame: 0,
        lastMoveTime: 0,
        lastFrameChangeTime: 0
      };
    },

    setPlayerAnimationState: (playerId, state) => {
      playerAnimationState.current.set(playerId, state);
    },

    updatePlayerAnimation: (playerId, updates) => {
      const current = playerAnimationState.current.get(playerId) || {
        direction: 'front',
        frame: 0,
        lastMoveTime: 0,
        lastFrameChangeTime: 0
      };
      playerAnimationState.current.set(playerId, { ...current, ...updates });
    },

    initializePlayerAnimation: (playerId) => {
      if (!playerAnimationState.current.has(playerId)) {
        playerAnimationState.current.set(playerId, {
          direction: 'front',
          frame: 0,
          lastMoveTime: 0,
          lastFrameChangeTime: 0
        });
      }
    },

    removePlayerAnimation: (playerId) => {
      playerAnimationState.current.delete(playerId);
      playerStateBuffers.current.delete(playerId);
    },

    // Player state buffer utilities
    getPlayerStateBuffer: (playerId) => {
      return playerStateBuffers.current.get(playerId) || [];
    },

    addPlayerStateToBuffer: (playerId, state) => {
      let buffer = playerStateBuffers.current.get(playerId);
      if (!buffer) {
        buffer = [];
        playerStateBuffers.current.set(playerId, buffer);
      }
      buffer.push(state);
      
      // Keep buffer size manageable
      while (buffer.length > 60) {
        buffer.shift();
      }
    },

    clearPlayerStateBuffer: (playerId) => {
      playerStateBuffers.current.delete(playerId);
    },

    // Bomb state buffer utilities
    getBombStateBuffer: (bombId) => {
      return bombStateBuffers.current.get(bombId) || [];
    },

    addBombStateToBuffer: (bombId, state) => {
      let buffer = bombStateBuffers.current.get(bombId);
      if (!buffer) {
        buffer = [];
        bombStateBuffers.current.set(bombId, buffer);
      }
      buffer.push(state);
      
      // Keep buffer size manageable
      while (buffer.length > 60) {
        buffer.shift();
      }
    },

    clearBombStateBuffer: (bombId) => {
      bombStateBuffers.current.delete(bombId);
    },

    // Global utilities
    clearAllAnimationData: () => {
      playerAnimationState.current.clear();
      playerStateBuffers.current.clear();
      bombStateBuffers.current.clear();
    },

    initializePlayersAnimation: (players) => {
      players.forEach(player => {
        if (!playerAnimationState.current.has(player.playerId)) {
          playerAnimationState.current.set(player.playerId, {
            direction: 'front',
            frame: 0,
            lastMoveTime: 0,
            lastFrameChangeTime: 0
          });
        }
      });
    }
  }), []);

  // Memoized state values
  const state = React.useMemo(() => ({
    playerAnimationState,
    playerStateBuffers,
    bombStateBuffers
  }), []);

  const value = React.useMemo(() => ({
    ...state,
    ...actions
  }), [state, actions]);

  return (
    <AnimationContext.Provider value={value}>
      {children}
    </AnimationContext.Provider>
  );
};

export default AnimationContext;